import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import Footer from "../../../../../../examples/Footer";
import CrudService from "services/cruds-service";
import countryOptions from "../../components/List/Countries";
import {TextField} from "@mui/material";
import { AddRowButton, RemoveRowButton } from "components/RowActions";
import { CrmPageHeader, CrmNotice, CrmActionButton, crmCardSx } from "../shared/CrmUi";
import { ReadOnlyBanner, ReadOnlyFieldset } from "components/ReadOnlyLock";

const regions = [
    "North America",
    "Latin America",
    "Europe",
    "Asia-Pacific (APAC)",
    "Middle East & North Africa (MENA)",
    "Sub-Saharan Africa",
    "CIS & Central Asia",
];

const industriesOptions = [
    { label: "Automotive & Spare Parts" },
    { label: "Consumer Electronics" },
    { label: "Pharmaceuticals & Medical Devices" },
    { label: "Industrial Machinery & Equipment" },
    { label: "FMCG (Fast Moving Consumer Goods)" },
    { label: "Textiles & Apparel" },
    { label: "Chemicals & Hazardous Materials" },
    { label: "Oil & Energy" },
    { label: "Food & Beverage (Dry / Reefer)" },
    { label: "Construction Materials" },
    { label: "E-commerce & Retail" },
    { label: "Agricultural Products & Inputs" },
    { label: "Metals & Raw Materials" },
    { label: "Aerospace & Aviation Parts" },
    { label: "Plastic, Rubber & Packaging" },
    { label: "Logistics & Freight Providers" },
    { label: "General" },
];

const incotermsOptions = [
    { label: "CFR" },
    { label: "CIF" },
    { label: "CIP" },
    { label: "CPT" },
    { label: "DAP" },
    { label: "DAT" },
    { label: "DPU" },
    { label: "DDP" },
    { label: "EXW" },
    { label: "FAS" },
    { label: "FCA" },
    { label: "FOB" },
    { label: "FOT" },
    { label: "FOR" },
];

const transportModes = [
    { label: "Road", value: "Road" },
    { label: "Ocean", value: "Sea" },
    { label: "Air", value: "Air" },
    { label: "Rail", value: "Rail" },
    { label: "Project Cargo", value: "Project Cargo" },
];

const filteredCountries = (region) =>
    region ? countryOptions.filter((c) => c.region === region) : [];

const blankMarket = {
    name: "",
    region: "",
    countries: [],
    city: "",
    shippingPreferences: [],
    serviceType: [],
    selectedIncoterm: [],
    industry: [],
    score: "",
};


const CreateStrategy = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const strategyId = searchParams.get("id");
    const isEditing = Boolean(strategyId);
    // An existing strategy opens read-only, like the TMS pages; "Edit" unlocks the form.
    const [readOnlyMode, setReadOnlyMode] = useState(isEditing);
    useEffect(() => {
        setReadOnlyMode(isEditing);
    }, [isEditing, strategyId]);
    const [strategyName, setStrategyName] = useState("");
    const [markets, setMarkets] = useState([blankMarket]);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");

    useEffect(() => {
        if (!isEditing) return;
        const fetchDetails = async () => {
            setLoading(true);
            try {
                const resp = await CrudService.getStrategyById(strategyId);
                const { strategyName, keyMarkets = [] } = resp.data.attributes;

                const normalizedMarkets = keyMarkets.map((m) => ({
                    ...m,
                    countries: (Array.isArray(m.countries) ? m.countries : [m.country])
                        .filter(Boolean)
                        .map((label) => countryOptions.find((c) => c.label === label) || { label }),
                    selectedIncoterm: (m.selectedIncoterm || []).map((label) => ({ label })),
                    serviceType: (m.serviceType || []).map((v) => ({ label: v, value: v })),
                    industry: (m.industry || []).map((label) => ({ label })),
                    shippingPreferences: (m.shippingPreferences || []).map((v) => ({ label: v, value: v })),
                    score: m.score?.toString() || "",
                }));
                setStrategyName(strategyName);
                setMarkets(normalizedMarkets.length ? normalizedMarkets : [blankMarket]);
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [isEditing, strategyId]);


    const totalScore = useMemo(
        () => markets.reduce((s, m) => s + (parseFloat(m.score) || 0), 0),
        [markets]
    );
    const hasFieldError = markets.some((m) => parseFloat(m.score) > 100);
    const hasTotalError = totalScore !== 100;
    const hasAnyError = hasFieldError || hasTotalError;
    const handleMarketChange = useCallback((idx, field, value) => {setMarkets((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));}, []);
    const addMarket = useCallback(() => setMarkets((prev) => [...prev, blankMarket]), []);
    const removeMarket = useCallback((idx) => {setMarkets((prev) => prev.filter((_, i) => i !== idx));}, []);

    const saveStrategy = useCallback(
        async (payload) => {
            setLoading(true);
            try {
                if (isEditing) {
                    await CrudService.updateStrategy(payload, strategyId);
                    setSuccessMessage("Update completed successfully.");
                } else {
                    await CrudService.createStrategy({ data: payload });
                    setSuccessMessage("Registration created successfully.");
                }
                window.scrollTo({ top: 0, behavior: "smooth" });
                setTimeout(() => navigate("/strategy/strategy-tables/details"), 300);
            } catch (err) {
                console.error("Request failed", err);
            } finally {
                setLoading(false);
            }
        },
        [isEditing, navigate, strategyId]
    );

    const submitHandler = useCallback(
        (e) => {
            e.preventDefault();
            if (hasAnyError) return;
            const payload = { strategyName, keyMarkets: markets };
            saveStrategy(payload);
        },
        [strategyName, markets, hasAnyError, saveStrategy]
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3}>
                <CrmPageHeader
                    title={isEditing ? "Edit Sales Strategy Card" : "Add Sales Strategy Card"}
                    subtitle="This information will help you manage your Sales Strategy Cards."
                />
                {successMessage && <CrmNotice sx={{ mb: 2 }}>{successMessage}</CrmNotice>}
                {readOnlyMode && (
                    <ReadOnlyBanner
                        message="This sales strategy is in read-only view. Click Edit to make changes."
                        onEdit={() => setReadOnlyMode(false)}
                    />
                )}
            </MDBox>
            <ReadOnlyFieldset readOnly={readOnlyMode}>
            <MDBox sx={{ ...crmCardSx, overflow: "visible", p: 3 }}>
                <MDBox mb={6} px={2}>
                    <TextField
                        label="Strategy Name"
                        fullWidth
                        value={strategyName}
                        placeholder="Enter Strategy Name"
                        onChange={(e) => setStrategyName(e.target.value)}
                    />
                </MDBox>
                {markets.map((mkt, idx) => (
                    <MDBox
                        key={idx}
                        mb={5}
                        p={3}
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            backgroundColor: "#fafafa",
                        }}
                    >
                        <Grid
                            container
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <MDTypography variant="h6" fontWeight="bold">
                                Key Market #{idx + 1}
                            </MDTypography>

                            <Grid item sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <TextField
                                    type="number"
                                    label="Score"
                                    value={mkt.score}
                                    onChange={(e) =>
                                        handleMarketChange(idx, "score", e.target.value)
                                    }
                                    sx={{ width: 120 }}
                                    inputProps={{ min: 0 }}
                                />

                                {markets.length > 1 && (
                                    <RemoveRowButton onClick={() => removeMarket(idx)} tooltip="Remove market" />
                                )}
                            </Grid>
                        </Grid>
                        {parseFloat(mkt.score) > 100 && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                Maximum score is 100
                            </Alert>
                        )}
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    label="Select Region"
                                    value={mkt.region}
                                    fullWidth
                                    onChange={(e) => {
                                        handleMarketChange(idx, "region", e.target.value);
                                        handleMarketChange(idx, "countries", []);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        "& .MuiInputBase-root": { height: 40 },
                                    }}
                                >
                                    {regions.map((r) => (
                                        <MenuItem key={r} value={r}>
                                            {r}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Autocomplete
                                    multiple
                                    disabled={!mkt.region}
                                    options={filteredCountries(mkt.region)}
                                    value={mkt.countries}
                                    onChange={(e, val) => handleMarketChange(idx, "countries", val)}
                                    getOptionLabel={(o) => o.label}
                                    isOptionEqualToValue={(o, v) => o.label === v.label}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Country"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="City"
                                    placeholder="Enter City"
                                    value={mkt.city}
                                    onChange={(e) => handleMarketChange(idx, "city", e.target.value)}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    sx={{
                                        "& .MuiInputBase-root": { height: 40 },
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={3}>
                                <Autocomplete
                                    multiple
                                    options={transportModes}
                                    value={mkt.shippingPreferences}
                                    onChange={(e, val) => handleMarketChange(idx, "shippingPreferences", val)}
                                    getOptionLabel={(o) => o.label}
                                    isOptionEqualToValue={(o, v) => o.value === v.value}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Service Mode"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Autocomplete
                                    multiple
                                    options={[
                                        { label: "Export", value: "Export" },
                                        { label: "Import", value: "Import" },
                                        { label: "Domestic", value: "Domestic" },
                                    ]}
                                    value={mkt.serviceType}
                                    onChange={(e, val) => handleMarketChange(idx, "serviceType", val)}
                                    getOptionLabel={(opt) => opt.label}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Service Type"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Autocomplete
                                    multiple
                                    options={incotermsOptions}
                                    value={mkt.selectedIncoterm}
                                    onChange={(e, val) => handleMarketChange(idx, "selectedIncoterm", val)}
                                    getOptionLabel={(o) => o.label}
                                    isOptionEqualToValue={(o, v) => o.label === v.label}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Incoterms"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Autocomplete
                                    multiple
                                    options={industriesOptions}
                                    value={mkt.industry}
                                    onChange={(e, val) => handleMarketChange(idx, "industry", val)}
                                    getOptionLabel={(o) => o.label}
                                    isOptionEqualToValue={(o, v) => o.label === v.label}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Industry"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                ))}
                <MDBox textAlign="center" mb={4}>
                    <AddRowButton onClick={addMarket} label="Add Key Market" />
                </MDBox>

                {/* TOTAL SCORE */}
                <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    color={hasTotalError ? "error" : "text"}
                >
                    Total Score: {totalScore}
                </MDTypography>

                {hasTotalError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        Total score must be exactly 100
                    </Alert>
                )}

                {/* SUBMIT BUTTON */}
                <MDBox textAlign="left" px={2} mt={2}>
                    <CrmActionButton onClick={submitHandler} disabled={loading || hasAnyError}>
                        {isEditing ? "Update Sales Strategy" : "Create Sales Strategy"}
                    </CrmActionButton>
                </MDBox>

            </MDBox>
            </ReadOnlyFieldset>

            <Footer />
        </DashboardLayout>
    );

};

export default CreateStrategy;
